import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Languages, Menu, X } from "lucide-react";
import { NAV, LOGO } from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const translate = (event) => {
    const language = event.target.value;
    if (!language) return;
    const url = encodeURIComponent(window.location.href);
    window.open(`https://translate.google.com/translate?sl=auto&tl=${language}&u=${url}`, "_blank", "noopener,noreferrer");
    event.target.value = "";
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-500 bg-ivory/90 backdrop-blur-xl ${
        scrolled ? "border-b border-sand/60 shadow-[0_10px_40px_-24px_rgba(26,54,38,0.5)]" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-5 md:px-10 h-24 md:h-28 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={LOGO.horizontal} alt="Travel Bug India - Discover India" className="h-20 md:h-24 w-auto object-contain drop-shadow-[0_5px_10px_rgba(10,25,47,0.12)]" />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.slice(1, 7).map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "") || "home"}`}
              className={`text-[0.82rem] font-medium tracking-wide transition-colors hover:text-gold ${
                pathname === n.to ? "text-gold" : "text-navy/80"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <label className="hidden lg:flex items-center gap-2 text-navy/65" title="Translate with Google">
            <Languages size={18} />
            <select defaultValue="" onChange={translate} aria-label="Translate website with Google" className="bg-transparent text-xs font-semibold outline-none cursor-pointer">
              <option value="">Language</option>
              <option value="de">Deutsch</option><option value="fr">Français</option><option value="es">Español</option><option value="it">Italiano</option><option value="nl">Nederlands</option>
            </select>
          </label>
          <Link
            to="/contact"
            data-testid="header-partner-cta"
            className="hidden sm:inline-flex items-center rounded-full bg-forest text-ivory px-6 py-2.5 text-[0.8rem] font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-0.5 hover:bg-teal"
          >
            Plan Your Journey
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden w-11 h-11 grid place-items-center text-forest"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden max-h-[calc(100dvh-6rem)] overflow-y-auto bg-ivory border-t border-sand/60 px-4 sm:px-5 py-4 sm:py-6">
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className={`min-h-11 flex items-center py-3 px-2 rounded-lg text-sm font-medium ${pathname === n.to ? "text-gold" : "text-navy/80"}`}
              >
                {n.label}
              </Link>
            ))}
            <Link to="/contact" data-testid="mobile-partner-cta" className="mt-3 text-center rounded-full bg-forest text-ivory px-6 py-3 text-sm font-semibold">
              Plan Your Journey
            </Link>
            <label className="mt-4 flex items-center gap-3 border-t border-sand pt-5 text-navy/65">
              <Languages size={18} />
              <select defaultValue="" onChange={translate} aria-label="Translate website with Google" className="flex-1 bg-transparent text-sm outline-none">
                <option value="">Translate with Google</option>
                <option value="de">Deutsch</option><option value="fr">Français</option><option value="es">Español</option><option value="it">Italiano</option><option value="nl">Nederlands</option>
              </select>
            </label>
          </nav>
        </div>
      )}
    </header>
  );
}
